import { useAppDispatch } from 'src/ui/app/hooks';
import {
  handleMutationError,
  handleMutationResult,
} from 'src/ui/containers/organization/hooks/helpers';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  useAddOrganizationInvitationsMutation,
  useDeleteInvitationsMutation,
  useRemoveOrganizationMembersMutation,
  useUpdateOrganizationMembersRoleMutation,
} from 'src/ui/graph/types';

export const useInviteOrgMembers = () => {
  const dispatch = useAppDispatch();
  const mutation = useAddOrganizationInvitationsMutation(graphQLClient);
  const mutateAsync = async (
    vars: Parameters<typeof mutation.mutateAsync>[0]
  ): Promise<boolean> => {
    try {
      const data = await mutation.mutateAsync(vars);
      return handleMutationResult(
        dispatch,
        data.addOrganizationInvitations,
        'Inviting User',
        vars.input.emails,
        (res) => res.email
      );
    } catch (err) {
      handleMutationError(dispatch, err, 'Inviting User', vars.input.emails);
      return false;
    }
  };
  return { ...mutation, mutateAsync };
};

export const useRemoveOrgInvites = () => {
  const dispatch = useAppDispatch();
  const mutation = useDeleteInvitationsMutation(graphQLClient);
  const mutateAsync = async (
    vars: Parameters<typeof mutation.mutateAsync>[0],
    emailsById: Record<string, string>
  ): Promise<boolean> => {
    const emails = Object.values(emailsById);
    try {
      const data = await mutation.mutateAsync(vars);
      return handleMutationResult(
        dispatch,
        data.deleteInvitations,
        'Removing Invitation',
        emails,
        (res) => emailsById[res.id]
      );
    } catch (err) {
      handleMutationError(dispatch, err, 'Removing Invitation', emails);
      return false;
    }
  };
  return { ...mutation, mutateAsync };
};

export const useRemoveOrgMembers = () => {
  const dispatch = useAppDispatch();
  const mutation = useRemoveOrganizationMembersMutation(graphQLClient);
  const mutateAsync = async (
    vars: Parameters<typeof mutation.mutateAsync>[0]
  ): Promise<boolean> => {
    const emails = Array.isArray(vars.emails) ? vars.emails : [vars.emails];
    try {
      const data = await mutation.mutateAsync(vars);
      return handleMutationResult(
        dispatch,
        data.removeOrganizationMembers,
        'Removing Member',
        emails,
        (res) => res.email
      );
    } catch (err) {
      handleMutationError(dispatch, err, 'Removing Member', emails);
      return false;
    }
  };
  return { ...mutation, mutateAsync };
};

export const useUpdateOrgMembersRole = () => {
  const dispatch = useAppDispatch();
  const mutation = useUpdateOrganizationMembersRoleMutation(graphQLClient);
  const mutateAsync = async (
    vars: Parameters<typeof mutation.mutateAsync>[0]
  ): Promise<boolean> => {
    const emails = Array.isArray(vars.emails) ? vars.emails : [vars.emails];
    try {
      const data = await mutation.mutateAsync(vars);
      return handleMutationResult(
        dispatch,
        data.updateOrganizationMembersRole,
        'Updating Role',
        emails,
        (res) => res.email
      );
    } catch (err) {
      handleMutationError(dispatch, err, 'Updating Role', emails);
      return false;
    }
  };
  return { ...mutation, mutateAsync };
};
